import React from 'react';
import { Button, Modal } from '@clearcover/atlas';
import {
  GetPortalPolicyVehicleInspectionsResponseV2,
  RemediateVehicleInspectionResponsePayload,
} from '@clearcover/core-app-types';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { useLazyApi } from 'Shared/services/Api';
import { Card, CardHeader, Loading, useToast } from 'Shared/components';
import { useModalHashChange } from 'Shared/hooks';
import { useAuth0 } from 'Auth';
import { editVehicleInspectionPolicy } from 'Admin/authPolicies';
import { AcceptedStatusType } from './types';
import { VehicleSetupRequestForm } from './VehicleSetupRequestForm';
import { VehicleInspectionRequest } from './VehicleInspectionRequest';

import styles from './styles.module.css';
import { ToastOptions } from 'Shared/components/Toast/Toast';

interface VehicleSetupStatusRequest {
  status: AcceptedStatusType;
  artifactRequestId: string;
  event?: string;
}

export const PolicyVehicleSetupSection = ({
  policyId,
  termId,
}: {
  policyId: string;
  termId?: string;
}) => {
  const { toast } = useToast();
  const { user } = useAuth0();
  const { modalHashChange } = useModalHashChange('vehicleSetupRequest');

  const [{ data: vehicleInspectionRequests, loading, refetch }] = useNewApiV2<
    GetPortalPolicyVehicleInspectionsResponseV2
  >(
    {
      url: `/vehicleInspection/${policyId}/requests${
        termId ? `?termId=${termId}` : ''
      }`,
    },
    false,
    false
  );

  const [{ loading: remediateLoading }, remediate] = useLazyApi<
    RemediateVehicleInspectionResponsePayload
  >({
    method: 'POST',
    url: `/vehicleInspection/remediate`,
  });

  const [{ loading: updateStatusLoading }, updateStatus] = useLazyApi<{}>({
    method: 'POST',
    url: `/vehicleInspection/request/status`,
  });

  const canEditVehicleInspection = editVehicleInspectionPolicy(
    user!.effectiveGrants
  );

  const vehicleOptions =
    vehicleInspectionRequests?.reduce((acc, request) => {
      acc[request.vehicleId] = request.vehicle;
      return acc;
    }, {} as { [key: string]: string }) || {};

  const pushRemediation = async (vehicleId: string) => {
    if (remediateLoading) return;

    const errorToast: ToastOptions = {
      message:
        'Vehicle setup overdue process could not be completed at this time',
      type: 'error',
      position: 'bottom',
    };

    try {
      const { data } = await remediate({
        data: {
          policyId,
          vehicles: [{ vehicleId }],
        },
      });

      if (data.vehicles[0]?.result.status === 'ERROR') {
        toast(errorToast);
        return;
      }
      toast({
        message: 'Vehicle setup overdue process complete!',
        type: 'success',
        position: 'bottom',
      });
      refetch();
    } catch {
      toast(errorToast);
    }
  };

  const changeStatus = async (
    requestId: string,
    status: AcceptedStatusType,
    event?: string
  ) => {
    if (updateStatusLoading) return;

    const errorToast: ToastOptions = {
      message: 'Something went wrong. Please try again',
      type: 'error',
      position: 'bottom',
    };

    try {
      const requestData: VehicleSetupStatusRequest = {
        status: status,
        artifactRequestId: requestId,
      };

      if (event) {
        requestData.event = event;
      }

      await updateStatus({
        data: requestData,
      });

      toast({
        message: 'Request has been successfully created',
        type: 'success',
        position: 'bottom',
      });
      refetch();
    } catch (error) {
      toast(errorToast);
      throw error;
    }
  };

  return (
    <Card id="vehicles-card">
      <CardHeader title={'Vehicle Setup'}>
        {canEditVehicleInspection && (
          <Modal
            onVisibilityChange={modalHashChange}
            variant="normal"
            toggle={openModal => (
              <Button
                variant="secondary"
                size="sm"
                onClick={openModal}
                data-testid="open-vehicle-setup-modal"
              >
                Create new request
              </Button>
            )}
          >
            {close => (
              <VehicleSetupRequestForm
                close={close}
                changeStatus={changeStatus}
                vehicleOptions={vehicleOptions}
                vehicleInspectionRequests={vehicleInspectionRequests}
              />
            )}
          </Modal>
        )}
      </CardHeader>
      {!loading && vehicleInspectionRequests?.length > 0 && (
        <ul className={styles.list}>
          <li key="title" className={styles.headers}>
            <span>Status</span>
            <span>Vehicle</span>
            <span>Setup event</span>
            <span>Date added to policy</span>
          </li>
          {vehicleInspectionRequests.map(request => {
            return (
              <VehicleInspectionRequest
                key={request.requestId}
                vehicleRequest={request}
                onChangeStatus={changeStatus}
                onRemediate={() => pushRemediation(request.vehicleId)}
              />
            );
          })}
        </ul>
      )}
      {loading && (
        <div className={styles.loader}>
          <Loading />
        </div>
      )}
    </Card>
  );
};
